import { useCallback, useEffect, useRef, useState } from 'react';
import type { MapAreaStyle, MapEntityArea, MapEntityPoint, MapEntityRef } from '../canvas/types';

// Helper to generate unique IDs
const generateUid = () => Math.random().toString(36).substring(2, 9);

function randomColor() {
    const palette = ['#4a90d9', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c'];
    return palette[Math.floor(Math.random() * palette.length)];
}

function defaultStyle(): MapAreaStyle {
    const color = randomColor();
    return { fillColor: color, fillOpacity: 0.2, borderColor: color, borderWidth: 2, dashPattern: [] };
}

export function useMapWorld(initialPoints?: MapEntityPoint[], initialAreas?: MapEntityArea[]) {
    const [mapEntityPoints, setMapEntityPoints] = useState<MapEntityPoint[]>([]);
    const [mapEntityAreas, setMapEntityAreas] = useState<MapEntityArea[]>([]);

    const [isPlacingPoint, setIsPlacingPoint] = useState(false);
    const [isDrawingArea, setIsDrawingArea] = useState(false);
    const [selectedWorldUid, setSelectedWorldUid] = useState<string | null>(null);

    // Picker state
    const [entityPickerOpen, setEntityPickerOpen] = useState(false);
    const [entityPickerLocationsOnly, setEntityPickerLocationsOnly] = useState(false);

    // Pending coordinates waiting for picker selection
    const pendingPointCoords = useRef<{ x: number; y: number } | null>(null);
    const pendingAreaVertices = useRef<{ x: number; y: number }[] | null>(null);

    // Drawing helpers
    const [inProgressVertices, setInProgressVertices] = useState<{ x: number; y: number }[]>([]);
    const [cursorVertex, setCursorVertex] = useState<{ x: number; y: number } | null>(null);

    // Sync state when DB loads
    useEffect(() => {
        if (initialPoints) {
            setMapEntityPoints(initialPoints);
        } else {
            setMapEntityPoints([]);
        }
    }, [initialPoints]);

    useEffect(() => {
        if (initialAreas) {
            setMapEntityAreas(initialAreas);
        } else {
            setMapEntityAreas([]);
        }
    }, [initialAreas]);

    // Area drawing vertices management
    const addVertex = useCallback((x: number, y: number) => {
        setInProgressVertices(prev => [...prev, { x, y }]);
    }, []);

    const handleAreaDrawn = useCallback((vertices: { x: number; y: number }[]) => {
        pendingAreaVertices.current = vertices;
        setIsDrawingArea(false);
        setEntityPickerLocationsOnly(true);
        setEntityPickerOpen(true);
    }, []);

    const finishDrawing = useCallback(() => {
        if (inProgressVertices.length >= 3) {
            handleAreaDrawn(inProgressVertices);
        }
        setInProgressVertices([]);
        setCursorVertex(null);
        setIsDrawingArea(false);
    }, [inProgressVertices, handleAreaDrawn]);

    const cancelDrawing = useCallback(() => {
        setInProgressVertices([]);
        setCursorVertex(null);
        setIsDrawingArea(false);
    }, []);

    // Point Placement
    const handlePointPlaced = useCallback((x: number, y: number) => {
        pendingPointCoords.current = { x, y };
        setIsPlacingPoint(false);
        setEntityPickerLocationsOnly(false);
        setEntityPickerOpen(true);
    }, []);

    // Entity Picker confirmation
    const handleEntityPicked = useCallback((ref: MapEntityRef) => {
        if (pendingPointCoords.current) {
            const uid = generateUid();
            const { x, y } = pendingPointCoords.current;
            setMapEntityPoints(prev => [...prev, { uid, entityRef: ref, x, y }]);
            pendingPointCoords.current = null;
        } else if (pendingAreaVertices.current) {
            const uid = generateUid();
            setMapEntityAreas(prev => [...prev, {
                uid,
                entityRef: { ...ref, type: 'locations' },
                vertices: pendingAreaVertices.current!,
                style: defaultStyle(),
            }]);
            pendingAreaVertices.current = null;
        }
        setEntityPickerOpen(false);
    }, []);

    const closePicker = useCallback(() => {
        setEntityPickerOpen(false);
        pendingPointCoords.current = null;
        pendingAreaVertices.current = null;
    }, []);

    // Keyboard handlers
    useEffect(() => {
        if (!isDrawingArea) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { e.preventDefault(); cancelDrawing(); }
            if (e.key === 'Enter') { e.preventDefault(); finishDrawing(); }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isDrawingArea, cancelDrawing, finishDrawing]);

    // CRUD functions
    const deletePoint = useCallback((uid: string) => {
        setMapEntityPoints(prev => prev.filter(p => p.uid !== uid));
        if (selectedWorldUid === uid) setSelectedWorldUid(null);
    }, [selectedWorldUid]);

    const changeLabelPoint = useCallback((uid: string, label: string) => {
        setMapEntityPoints(prev => prev.map(p => p.uid === uid ? { ...p, label: label || undefined } : p));
    }, []);

    const changeEntityPoint = useCallback((uid: string, ref: MapEntityRef) => {
        setMapEntityPoints(prev => prev.map(p => p.uid === uid ? { ...p, entityRef: ref } : p));
    }, []);

    const deleteArea = useCallback((uid: string) => {
        setMapEntityAreas(prev => prev.filter(a => a.uid !== uid));
        if (selectedWorldUid === uid) setSelectedWorldUid(null);
    }, [selectedWorldUid]);

    const updateAreaStyle = useCallback((uid: string, style: Partial<MapAreaStyle>) => {
        setMapEntityAreas(prev => prev.map(a => a.uid === uid ? { ...a, style: { ...a.style, ...style } } : a));
    }, []);

    const changeEntityArea = useCallback((uid: string, ref: MapEntityRef) => {
        setMapEntityAreas(prev => prev.map(a => a.uid === uid ? { ...a, entityRef: { ...ref, type: 'locations' } } : a));
    }, []);

    return {
        mapEntityPoints,
        setMapEntityPoints,
        mapEntityAreas,
        setMapEntityAreas,
        isPlacingPoint,
        setIsPlacingPoint,
        isDrawingArea,
        setIsDrawingArea,
        selectedWorldUid,
        setSelectedWorldUid,
        entityPickerOpen,
        entityPickerLocationsOnly,
        inProgressVertices,
        cursorVertex,
        setCursorVertex,
        addVertex,
        finishDrawing,
        cancelDrawing,
        handlePointPlaced,
        handleAreaDrawn,
        handleEntityPicked,
        closePicker,
        deletePoint,
        changeLabelPoint,
        changeEntityPoint,
        deleteArea,
        updateAreaStyle,
        changeEntityArea,
    };
}
